import { uploadSignatureFilestack, type IntentEnum } from '@goldcast/api/backend';
import * as filestack from 'filestack-js';

import appLogger from '@/services/logger';
import { getEnvConfig } from '@/constants';

export interface FilestackSecurity {
  policy: string;
  signature: string;
}

type FilestackClient = ReturnType<typeof filestack.init>;
type FilestackUploadOptions = Parameters<FilestackClient['upload']>[1];
type FilestackStoreOptions = Parameters<FilestackClient['upload']>[2];
type FilestackUploadToken = Parameters<FilestackClient['upload']>[3];
type FilestackMultiUploadOptions = Parameters<FilestackClient['multiupload']>[1];
type FilestackMultiStoreOptions = Parameters<FilestackClient['multiupload']>[2];

interface UploadFileWithFilestackSecurityRetryArgs {
  client?: FilestackClient;
  file: File;
  uploadOptions: FilestackUploadOptions;
  storeOptions: FilestackStoreOptions;
  filestackIntent: FilestackIntentArgs;
  uploadToken?: FilestackUploadToken;
}

interface UploadFilesWithFilestackSecurityRetryArgs {
  client?: FilestackClient;
  files: File[];
  uploadOptions: FilestackMultiUploadOptions;
  storeOptions: FilestackMultiStoreOptions;
  filestackIntent: FilestackIntentArgs;
}

export type FilestackIntentArgs =
  | { intent: Extract<IntentEnum, 'organization_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'event_asset' | 'slide_filler_asset'>; assetKind: string; event: string }
  | {
      intent: Extract<IntentEnum, 'content_lab_custom_asset'>;
      assetKind: string;
      projectId: string;
      contentId: string;
    }
  | { intent: Extract<IntentEnum, 'brand_kit_asset'>; assetKind: string }
  | { intent: Extract<IntentEnum, 'clip_overlay_asset' | 'content_lab_root_asset'>; assetKind: string };

function buildBody(args: FilestackIntentArgs, sizeBytes?: number): Record<string, string | number> {
  const body: Record<string, string | number> = { intent: args.intent, asset_kind: args.assetKind };
  if (sizeBytes !== undefined) {
    body.size_bytes = sizeBytes;
  }
  if (args.intent === 'event_asset' || args.intent === 'slide_filler_asset') {
    body.event = args.event;
  } else if (args.intent === 'content_lab_custom_asset') {
    body.project_id = args.projectId;
    body.content_id = args.contentId;
  }
  return body;
}

function resolveSizeHintBytes(sizeBytes?: number): number | undefined {
  if (sizeBytes === undefined || !Number.isFinite(sizeBytes) || sizeBytes <= 0) {
    return undefined;
  }
  return sizeBytes;
}

const FILESTACK_REQUEST_ERROR_TYPE = 'request';
const FILESTACK_AUTH_STATUS_CODES = new Set([401, 403]);
const FILESTACK_SECURITY_ERROR_TOKENS = ['policy', 'signature', 'security', 'expired', 'unauthorized', 'forbidden'];

function getFilestackRequestDetails(error: Record<string, unknown>): Record<string, unknown> | null {
  if (error.type !== FILESTACK_REQUEST_ERROR_TYPE || !error.details || typeof error.details !== 'object') {
    return null;
  }
  return error.details as Record<string, unknown>;
}

function stringifyErrorDetail(value: unknown): string {
  if (typeof value === 'string') {
    return value;
  }
  if (value && typeof value === 'object') {
    return JSON.stringify(value);
  }
  return '';
}

export function isFilestackSecurityError(error: unknown): boolean {
  if (!error || typeof error !== 'object') {
    return false;
  }

  const maybeError = error as Record<string, unknown>;
  const requestDetails = getFilestackRequestDetails(maybeError);
  if (!requestDetails) {
    return false;
  }

  if (typeof requestDetails.code === 'number' && FILESTACK_AUTH_STATUS_CODES.has(requestDetails.code)) {
    return true;
  }

  const searchableText = [maybeError.message, requestDetails.data].map(stringifyErrorDetail).join(' ').toLowerCase();

  return FILESTACK_SECURITY_ERROR_TOKENS.some(token => searchableText.includes(token));
}

// Deliberately uncached: the expiry is scaled to the size hint, so a reused policy would hand the
// next upload a partially consumed lifetime.
export async function getFilestackSecurity(
  args: FilestackIntentArgs,
  options: { sizeBytes?: number } = {}
): Promise<{ security: FilestackSecurity }> {
  const sizeHintBytes = resolveSizeHintBytes(options.sizeBytes);
  const response = (await uploadSignatureFilestack({ body: buildBody(args, sizeHintBytes) as never })) as {
    policy: string;
    signature: string;
  };
  return { security: { policy: response.policy, signature: response.signature } };
}

export async function uploadWithFilestackSecurityRetry<T>(
  args: FilestackIntentArgs,
  upload: (security: FilestackSecurity) => Promise<T>,
  sizeBytes?: number
): Promise<T> {
  let security: FilestackSecurity;
  try {
    ({ security } = await getFilestackSecurity(args, { sizeBytes }));
  } catch (error) {
    appLogger.error('Filestack signing failed', error);
    throw error;
  }
  try {
    return await upload(security);
  } catch (error) {
    if (!isFilestackSecurityError(error)) {
      throw error;
    }
    const { security: refreshedSecurity } = await getFilestackSecurity(args, { sizeBytes });
    return upload(refreshedSecurity);
  }
}

export async function uploadFileWithFilestackSecurityRetry({
  client,
  file,
  uploadOptions,
  storeOptions,
  filestackIntent,
  uploadToken
}: UploadFileWithFilestackSecurityRetryArgs): ReturnType<FilestackClient['upload']> {
  return uploadWithFilestackSecurityRetry(
    filestackIntent,
    security => {
      const filestackClient = client ?? filestack.init(getEnvConfig('FILESTACK_API_KEY'));
      return filestackClient.upload(file, uploadOptions, storeOptions, uploadToken, security);
    },
    file.size
  );
}

export async function uploadFilesWithFilestackSecurityRetry({
  client,
  files,
  uploadOptions,
  storeOptions,
  filestackIntent
}: UploadFilesWithFilestackSecurityRetryArgs): ReturnType<FilestackClient['multiupload']> {
  // The policy has to outlive the whole batch, so size the hint off the total, not the largest file.
  return uploadWithFilestackSecurityRetry(
    filestackIntent,
    security => {
      const filestackClient = client ?? filestack.init(getEnvConfig('FILESTACK_API_KEY'));
      return filestackClient.multiupload(files, uploadOptions, storeOptions, undefined, security);
    },
    files.reduce((total, file) => total + file.size, 0)
  );
}
